import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import { TextField, Autocomplete, Typography } from "@mui/material";
import { withTranslation } from "react-i18next";

const SelectField = ({
  label = "",
  name = "",
  options = [],
  disabled = false,
  formik = null,
  getOptionLabel = null,
  getOptionValue = null,
  placeholder = "",
  t,
  // eslint-disable-next-line no-unused-vars, react/prop-types
  tReady,
  ...props
}) => {
  const { touched, errors, setFieldValue, setFieldTouched, values } =
    formik || {};

  const errorMessage = useMemo(
    () => get(errors, name, ""),
    [get(errors, name, "")]
  );

  const isError = useMemo(
    () => !disabled && !!get(touched, name, "") && !!errorMessage,
    [disabled, get(touched, name, ""), errorMessage]
  );

  const selectOptions = useMemo(
    () =>
      getOptionLabel || getOptionValue
        ? options.map((option) => ({
            label: getOptionLabel ? getOptionLabel(option) : option.label,
            value: getOptionValue ? getOptionValue(option) : option.value,
          }))
        : options,
    [options]
  );

  const formikProps = formik
    ? {
        name,
        onChange: (_, option) => setFieldValue(name, option.value),
        onBlur: () => setFieldTouched(name, true),
        value:
          selectOptions?.find(
            (option) => option?.value === get(values, name, "")
          ) || null,
        renderInput: (params) => (
          <TextField {...params} label={label} error={isError} />
        ),
      }
    : {};

  return (
    <div className="mb-1">
      <Autocomplete
        className="w-100"
        size="small"
        options={selectOptions}
        disabled={disabled}
        placeholder={t(placeholder)}
        renderInput={(params) => <TextField {...params} label={label} />}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          );
        }}
        componentsProps={{
          popper: {
            popperOptions: {
              strategy: "fixed",
            },
          },
        }}
        disableClearable
        {...formikProps}
        {...props}
      />
      <Typography
        variant="subtitle2"
        className={`${
          isError ? "visible" : "invisible"
        } input-field-error-message`}
        color="error"
      >
        {t(`${errorMessage}`)}
      </Typography>
    </div>
  );
};

SelectField.propTypes = {
  formik: PropTypes.object,
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  disabled: PropTypes.bool,
  changeHandler: PropTypes.func,
  selectValue: PropTypes.number,
  placeholder: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(SelectField);
